import { sampleSize, shuffle } from "lodash-es";
import { memo, useEffect, useMemo, useRef } from "react";
import ReactFocusLock from "react-focus-lock";

import { useGame } from "~/components/GameContext";

import type { GameWord } from "../Game";
import type { GameAnswer } from "../utils/answer";

const Choices: React.FC<{
  word: GameWord;
  onAnswer: React.Dispatch<GameAnswer>;
  isDisabled: boolean;
}> = ({ word, onAnswer, isDisabled }) => {
  const { game, playSound } = useGame();
  const gridRef = useRef<HTMLDivElement>(null);

  const choices = useMemo(() => {
    const candidates = game.words.filter((w) => {
      if (w.solution.toLowerCase() === word.solution.toLowerCase()) {
        return false;
      }

      if (
        game.mode === "imcq" &&
        word.definitions.some((definition) => definition.image)
      ) {
        // only consider words with images as candidates for other image clues
        // when in image match mode
        return w.definitions.some((definition) => definition.image);
      }

      return true;
    });
    return shuffle([word, ...sampleSize(candidates, 2)]);
  }, [word, game.words, game.mode]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        !event.repeat &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey &&
        !event.shiftKey &&
        (event.key === "1" || event.key === "2" || event.key === "3")
      ) {
        const index = Number(event.key) - 1;
        gridRef.current?.querySelectorAll("button")[index]?.click();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <ReactFocusLock
      ref={gridRef}
      disabled={isDisabled}
      autoFocus={false}
      className=" absolute inset-0 grid auto-cols-fr max-md:divide-y md:grid-flow-col md:divide-x group-data-[row='true']:lg:grid-flow-row group-data-[row='true']:lg:divide-x-0 group-data-[row='true']:lg:divide-y"
    >
      {choices.map((choice, index) => {
        const imageClue = choice.definitions.find(
          (definition) => definition.image,
        );
        return (
          <div key={choice.id} className="flex">
            <button
              className="relative grow text-2xl transition-colors focus-visible:outline-none focus-visible:ring focus-visible:ring-inset active:bg-base-200 md:text-3xl"
              onClick={() => {
                const isCorrect = choice.id === word.id;
                onAnswer({
                  correct: isCorrect,
                  word,
                  solution: word.solution,
                });
                playSound(isCorrect ? "correct" : "skip");
              }}
            >
              {game.mode === "imcq" && imageClue?.image ? (
                <div className="absolute inset-4">
                  <img
                    className="size-full object-contain"
                    alt={imageClue.text}
                    src={imageClue.image}
                  />
                </div>
              ) : (
                choice.solution
              )}
              <div className="kbd kbd-sm absolute bottom-4 right-4 max-md:hidden">
                {index + 1}
              </div>
            </button>
          </div>
        );
      })}
    </ReactFocusLock>
  );
};

export default memo(Choices);
